import React, { useEffect, useState } from "react";
import Select from "react-select"; // Import react-select
import { toast } from "react-toastify";
import { getUsers } from "../../api/apiGetrequest";
import {
  AddDriveImage,
  AddDriverLincense,
  UpdateDriverLincense,
} from "../../api/apirequest";

const LicenseForm = ({ licence, fetchData, DriverID }) => {
  const [formData, setFormData] = useState({
    licenseNO: licence?.LicenseNO || "",
    nextRenewalDate: licence?.NextRenewalDate || "",
    licenseImage: null,
    driverID: licence?.DriverID || DriverID || null, // Use DriverID from props or licence
    selectedDriver: null, // Pre-selected driver during editing
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [drivers, setDrivers] = useState([]);

  // Map drivers to react-select format
  const driverOptions = drivers.map((driver) => ({
    value: driver.UserID,
    label: `${driver.FullName} (${driver.VehicleRegistrationNumber})`,
  }));

  // Fetch users and pre-select the driver if editing
  const fetchUsers = async () => {
    try {
      const result = await getUsers();
      const driverList = result?.filter((d) => d.Role === "driver");
      setDrivers(driverList);

      // Pre-select the driver if editing
      if (licence?.DriverID) {
        const selectedDriver = driverList.find(
          (driver) => driver.UserID === licence.DriverID
        );
        if (selectedDriver) {
          setFormData((prev) => ({
            ...prev,
            selectedDriver: {
              value: selectedDriver.UserID,
              label: `${selectedDriver.FullName} (${selectedDriver.VehicleRegistrationNumber})`,
            },
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [licence]); // Re-fetch when licence prop changes

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "licenseImage") {
      setFormData({
        ...formData,
        [name]: files[0], // Store the selected file
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleDriverChange = (selectedOption) => {
    setFormData({
      ...formData,
      selectedDriver: selectedOption,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Validate form fields
      if (
        !formData.licenseNO ||
        !formData.nextRenewalDate ||
        !formData.licenseImage
      ) {
        setError("Please fill in all required fields.");
        return;
      }

      // If no DriverID is provided, ensure a driver is selected
      if (!DriverID && !formData.selectedDriver) {
        setError("Please select a driver.");
        return;
      }

      // Convert image to base64
      const reader = new FileReader();
      reader.readAsDataURL(formData.licenseImage);
      reader.onload = async () => {
        const base64Image = reader.result.split(",")[1]; // Remove the data URL prefix

        // Upload image to drive
        const imageResult = await AddDriveImage({
          base64Image: base64Image,
          fileName: new Date().toISOString(),
        });

        if (imageResult.ViewLink) {
          const { FileId } = imageResult;

          // Determine the driver ID
          const driverID = DriverID || formData.selectedDriver.value;

          // Check if updating or adding a new license
          if (licence?.LicenseNO) {
            // Update existing license
            const updateData = {
              licenseID: licence.LicenseID, // Use the existing license ID
              nextRenewalDate: formData.nextRenewalDate,
              licenseImage: FileId,
              dateUpdated: new Date().toISOString(),
              statusID: 2, // Set status to "Pending Approval"
            };

            const result = await UpdateDriverLincense(updateData);

            if (result && result.success) {
              toast.success(result.message || "License successfully updated!");
            } else {
              toast.error(
                result.message ||
                  "An error occurred while updating the license."
              );
            }
          } else {
            // Add new license
            const addData = {
              licenseNO: formData.licenseNO,
              nextRenewalDate: formData.nextRenewalDate,
              licenseImage: FileId,
              driverID: driverID, // Use the selected driver ID
              dateAdded: new Date().toISOString(),
              dateUpdated: new Date().toISOString(),
              statusID: 2, // Set status to "Pending Approval"
            };

            const result = await AddDriverLincense(addData);

            if (result && result.success) {
              toast.success(result.message || "License successfully added!");
            } else {
              toast.error(
                result.message || "An error occurred while adding the license."
              );
            }
          }

          // Refresh data and reset form
          fetchData();
          setFormData({
            licenseNO: "",
            nextRenewalDate: "",
            licenseImage: null,
            selectedDriver: null, // Reset driver selection
          });
        } else {
          toast.error(
            imageResult.message || "Failed to upload the image to the drive."
          );
        }
      };
    } catch (error) {
      console.error(error);
      toast.error("There was an error processing your request.");
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    formContainer: {
      maxWidth: "400px",
      margin: "0 auto",
      padding: "20px",
      border: "1px solid #ccc",
      borderRadius: "5px",
      backgroundColor: "#f9f9f9",
    },
    formGroup: {
      marginBottom: "15px",
    },
    label: {
      display: "block",
      marginBottom: "5px",
      fontWeight: "bold",
    },
    input: {
      width: "100%",
      padding: "8px",
      boxSizing: "border-box",
      border: "1px solid #ccc",
      borderRadius: "4px",
    },
    submitButton: {
      width: "100%",
      padding: "10px",
      backgroundColor: "#4CAF50",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      opacity: loading ? 0.7 : 1,
      pointerEvents: loading ? "none" : "auto",
    },
    error: {
      color: "red",
      marginBottom: "15px",
      textAlign: "center",
    },
    disabledInput: {
      backgroundColor: "#f0f0f0",
      cursor: "not-allowed",
    },
  };

  return (
    <div style={styles.formContainer}>
      <h2>{licence?.LicenseNO ? "Update License" : "Add License"}</h2>
      {error && <div style={styles.error}>{error}</div>}
      <form onSubmit={handleSubmit}>
        {/* Driver Selection Dropdown */}
        {!DriverID && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Select Driver:</label>
            {licence?.DriverID ? (
              <input
                type="text"
                value={formData.selectedDriver?.label || ""}
                style={{ ...styles.input, ...styles.disabledInput }}
                disabled
              />
            ) : (
              <Select
                options={driverOptions}
                value={formData.selectedDriver}
                onChange={handleDriverChange}
                placeholder="Select a driver..."
                isSearchable
                required
              />
            )}
          </div>
        )}

        <div style={styles.formGroup}>
          <label style={styles.label}>License Number:</label>
          <input
            type="text"
            name="licenseNO"
            value={formData.licenseNO}
            onChange={handleChange}
            style={styles.input}
            required
            disabled={licence?.LicenseNO} // Disable if updating
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Next Renewal Date:</label>
          <input
            type="datetime-local"
            name="nextRenewalDate"
            value={formData.nextRenewalDate}
            onChange={handleChange}
            style={styles.input}
            required
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>License Image:</label>
          <input
            type="file"
            name="licenseImage"
            onChange={handleChange}
            style={styles.input}
            accept="image/*"
            required
          />
        </div>

        <button type="submit" style={styles.submitButton} disabled={loading}>
          {loading ? "Submitting..." : "Submit"}
        </button>
      </form>
    </div>
  );
};

export default LicenseForm;